import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PersonIcon from '@mui/icons-material/Person';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SearchScope } from '../api/searchTypes';

export interface ScopeChipsProps {
    scope: SearchScope;
    // schema.scopes: the Traders button shows only once the indexer serves it.
    scopes: readonly SearchScope[];
    // Off while the schema loads: the switch needs the new scope's fields.
    disabled?: boolean;
    onChange: (scope: SearchScope) => void;
}

const CHIPS: {
    scope: SearchScope;
    label: string;
    hint: string;
    Icon: SvgIconComponent;
}[] = [
    {
        scope: 'event',
        label: 'Events',
        hint: 'Match the event as a whole',
        Icon: EventNoteIcon,
    },
    {
        scope: 'market',
        label: 'Markets',
        hint: 'Match each market on its own',
        Icon: ShowChartIcon,
    },
    {
        scope: 'trader',
        label: 'Traders',
        hint: 'Find traders by activity, size and performance',
        Icon: PersonIcon,
    },
];

// The type switch of spec 6.10: a full-width segmented control over the
// filter column, as tall as the search bar.
export function ScopeChips({
    scope,
    scopes,
    disabled = false,
    onChange,
}: ScopeChipsProps) {
    const chips = CHIPS.filter(
        c => c.scope !== 'trader' || scopes.includes('trader')
    );
    return (
        <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={scope}
            disabled={disabled}
            // A click on the active button reports null.
            onChange={(_, next: SearchScope | null) => {
                if (next) onChange(next);
            }}
            aria-label="Search type"
            sx={{ height: 40 }}
        >
            {chips.map(({ scope: value, label, hint, Icon }) => (
                <Tooltip
                    key={value}
                    // A disabled button fires no events, so no hint then.
                    title={disabled ? '' : hint}
                    describeChild
                    disableInteractive
                >
                    <ToggleButton
                        value={value}
                        sx={{
                            // Never wider than its share of the group, even
                            // in a fallback font: the label shrinks instead.
                            minWidth: 0,
                            gap: 0.5,
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            // The event chart's Stacked/Line/Candles toggle
                            // (ChartToolbar.tsx): standard toggle colours.
                            borderColor: 'divider',
                        }}
                    >
                        <Icon sx={{ fontSize: 16, flexShrink: 0 }} />
                        <Box
                            component="span"
                            sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {label}
                        </Box>
                    </ToggleButton>
                </Tooltip>
            ))}
        </ToggleButtonGroup>
    );
}
